import { TodoTask } from '../add-todo/addTodoList';
import { todoApi } from '../../services/api';

class TodoManager {
    private static instance: TodoManager;
    private todos: TodoTask[] = [];
    private initializationPromise: Promise<void>;
    private listeners: (() => void)[] = [];
  
    private constructor() {
      this.initializationPromise = this.initializeTodos();
    } 

    // Method to register listeners
    public subscribe(callback: () => void): () => void {
      this.listeners.push(callback);
      // Return unsubscribe function
      return () => {
          this.listeners = this.listeners.filter(listener => listener !== callback);
      };
    }

    // Method to notify all listeners
    private notifyListeners(): void {
        this.listeners.forEach(listener => listener());
    }
  
    private async initializeTodos(): Promise<void> {
      try {
        const todosFromServer = await todoApi.getAllTodos();
        this.todos = todosFromServer;
        console.log('from server')
        console.log(this.todos.length);
      } catch (error) {
        console.error('Failed to initialize todos:', error);
      }
    }

    // Get the single instance
    public static getInstance(): TodoManager {
      if (!TodoManager.instance) {
        TodoManager.instance = new TodoManager();
      }
      return TodoManager.instance;
    }
    
    // Get all todos
    public async getTodos(): Promise<TodoTask[]> {
      // Wait for initialization to complete before returning todos
      await this.initializationPromise;
      return this.todos;
    }
  
    // Add a todo
    public async addTodo(todo: TodoTask): Promise<void> {
      try {
        const createdTodo = await todoApi.createTodo(todo);
        this.todos.push(createdTodo);
        this.notifyListeners();
      } catch (error) {
        console.error('Failed to add todo:', error);
        throw error; // Rethrow to handle in UI
      }
    }
  
    // Delete a todo
    public async deleteTodo(id: string): Promise<void> {
      try {
        await todoApi.deleteTodo(id);
        this.todos = this.todos.filter(todo => todo._id !== id);
        this.notifyListeners();
      } catch (error) {
        console.error('Failed to delete todo:', error);
        throw error;
      }
    }
  
    // Update a todo
    public async updateTodo(updatedTodo: TodoTask): Promise<void> {
      try {
        const updated = await todoApi.updateTodo(
          updatedTodo._id, 
          updatedTodo
        );
        const index = this.todos.findIndex(todo => todo._id === updatedTodo._id);
        if (index !== -1) {
          this.todos[index] = updated;
        }
        this.notifyListeners();
      } catch (error) {
        console.error('Failed to update todo:', error);
        throw error;
      }
    }

    public async updateTodoComplete(updatedTodo: TodoTask, isCompleted: boolean): Promise<void> {
      try {
        const updated = await todoApi.updateTodo(
          updatedTodo._id,
          { ...updatedTodo, completed: isCompleted }
        );
        const index = this.todos.findIndex(todo => todo._id === updatedTodo._id);
        if (index !== -1) {
          this.todos[index] = updated;
          console.log(this.todos[index].completed);
        }
        this.notifyListeners();
        //await this.refreshTodos();
      } catch (error) {
        console.error('Failed to update todo completion:', error);
        throw error;
      }
    }

    public async updateTodoDate(updatedTodo: TodoTask, date: Date): Promise<void> {
      try {
        const updated = await todoApi.updateTodo(
          updatedTodo._id,
          { ...updatedTodo, date: date }
        );
        const index = this.todos.findIndex(todo => todo._id === updatedTodo._id);
        if (index !== -1) {
          this.todos[index] = updated;
        }
      } catch (error) {
        console.error('Failed to update todo completion:', error);
        throw error;
      }
    }

    // Optional: Method to manually refresh todos from server
    public async refreshTodos(): Promise<void> {
      try {
        const todosFromServer = await todoApi.getAllTodos();
        this.todos = todosFromServer;
      } catch (error) {
        console.error('Failed to refresh todos:', error);
        throw error;
      }
    }

    private readonly DRAFT_TODO_KEY = 'draft_todo';

    // Save draft todo to localStorage
    public saveDraftTodo(todo: TodoTask): void {
      try {
        localStorage.setItem(this.DRAFT_TODO_KEY, JSON.stringify(todo));
      } catch (error) {
        console.error('Failed to save draft todo:', error);
      }
    }

    // Get draft todo from localStorage
    public getDraftTodo(): TodoTask {
      try {
        const draftTodo = localStorage.getItem(this.DRAFT_TODO_KEY);
        console.log("Retrieved from localStorage:", draftTodo);
        
        if (draftTodo !== null && draftTodo !== undefined) {
          console.log("draftTodo exists");
          const parsed = JSON.parse(draftTodo);
          // Convert date string back to Date object
          return {
            ...parsed,
            date: new Date(parsed.date)
          };
        }
      } catch (error) {
        console.error('Failed to get draft todo:', error);
      }
      
      // Default todo if nothing in localStorage or if there was an error
      return {
        id: Date.now(), // Note: Using _id instead of id to match your TodoTask interface
        title: "",
        completed: false,
        category: 'Work',
        date: new Date(),
        content: "",
      };
    }

    // Clear draft todo from localStorage
    public clearDraftTodo(): void {
      try {
        localStorage.removeItem(this.DRAFT_TODO_KEY);
      } catch (error) {
        console.error('Failed to clear draft todo:', error);
      }
    }
}
  
export default TodoManager;